import { haversine } from './geo';
import type { LatLng } from './types';

/**
 * Landmarks for search. The API has no places/geocoding service yet, so search is this local list plus
 * "Set on map". Coordinates are approximate (placed from OpenStreetMap by eye) and need checking on the ground
 * before launch — see docs/OWNER-DECISIONS.md.
 */
export type Landmark = LatLng & {
  id: string;
  /** Short name used on chips and trip cards: "Main Bazaar". */
  name: string;
  /** Second line: area / town. */
  area: string;
  /** Small spots ("SBI ATM") are used for "near …" hints; big ones are destinations. */
  kind: 'place' | 'spot';
  aliases?: string[];
};

export const LANDMARKS: Landmark[] = [
  { id: 'main-bazaar', name: 'Main Bazaar', area: 'Jourian', kind: 'place', lat: 32.8337, lng: 74.5829, aliases: ['bazar', 'market'] },
  { id: 'sbi-atm', name: 'SBI ATM', area: 'Main Bazaar, Jourian', kind: 'spot', lat: 32.8344, lng: 74.5839, aliases: ['bank', 'atm'] },
  { id: 'jourian-bus-stand', name: 'Bus Stand', area: 'Jourian', kind: 'place', lat: 32.832, lng: 74.5865, aliases: ['jourian bus stand', 'bus adda'] },
  { id: 'degree-college', name: 'Degree College', area: 'Jourian', kind: 'place', lat: 32.841, lng: 74.5958, aliases: ['college'] },
  { id: 'ward-4', name: 'Ward 4', area: 'Jourian', kind: 'place', lat: 32.8268, lng: 74.5745 },
  { id: 'police-station', name: 'Police Station', area: 'Jourian', kind: 'spot', lat: 32.8355, lng: 74.581, aliases: ['thana'] },
  { id: 'phc-jourian', name: 'Health Centre', area: 'Jourian', kind: 'place', lat: 32.8362, lng: 74.588, aliases: ['hospital', 'phc'] },
  { id: 'chenab-ghat', name: 'Chenab Ghat', area: 'Jourian', kind: 'spot', lat: 32.83, lng: 74.576, aliases: ['river', 'ghat'] },
  { id: 'station-rd', name: 'Station Rd', area: 'Jourian', kind: 'spot', lat: 32.8365, lng: 74.5852 },
  { id: 'manchak', name: 'Manchak', area: 'Akhnoor road', kind: 'place', lat: 32.861, lng: 74.656, aliases: ['manchak turn'] },
  { id: 'akhnoor-bridge', name: 'Akhnoor Bridge', area: 'Akhnoor', kind: 'place', lat: 32.893, lng: 74.74, aliases: ['bridge', 'pul'] },
  { id: 'akhnoor-fort', name: 'Akhnoor Fort', area: 'Fort Road, Akhnoor', kind: 'place', lat: 32.8967, lng: 74.7377, aliases: ['qila', 'fort'] },
  { id: 'akhnoor-bus-stand', name: 'Akhnoor Bus Stand', area: 'Akhnoor', kind: 'place', lat: 32.8885, lng: 74.7425 },
  { id: 'akhnoor-market', name: 'Akhnoor Main Market', area: 'Akhnoor', kind: 'place', lat: 32.89, lng: 74.744 },
  { id: 'akhnoor-hospital', name: 'Sub-District Hospital', area: 'Akhnoor', kind: 'place', lat: 32.886, lng: 74.747, aliases: ['hospital', 'sdh'] },
  { id: 'jammu-tawi', name: 'Jammu Tawi Railway Station', area: 'Jammu', kind: 'place', lat: 32.7061, lng: 74.8817, aliases: ['railway', 'station', 'train'] },
  { id: 'jammu-bus-stand', name: 'General Bus Stand', area: 'Jammu', kind: 'place', lat: 32.7311, lng: 74.864, aliases: ['jammu bus stand'] },
];

const norm = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9 ]/g, '').trim();

export type Match = { landmark: Landmark; /** length of the matched prefix of `name`, for highlighting */ highlight: number };

/** Prefix matches on the name first, then word / alias matches. */
export function searchLandmarks(query: string, list: Landmark[] = LANDMARKS, limit = 6): Match[] {
  const q = norm(query);
  if (!q) return [];
  const scored: { m: Match; score: number }[] = [];
  for (const l of list) {
    const name = norm(l.name);
    if (name.startsWith(q)) scored.push({ m: { landmark: l, highlight: query.trim().length }, score: 0 });
    else if (name.split(' ').some((w) => w.startsWith(q))) scored.push({ m: { landmark: l, highlight: 0 }, score: 1 });
    else if ([l.area, ...(l.aliases ?? [])].some((a) => norm(a).split(' ').some((w) => w.startsWith(q)) || norm(a).startsWith(q)))
      scored.push({ m: { landmark: l, highlight: 0 }, score: 2 });
  }
  return scored
    .sort((a, b) => a.score - b.score || a.m.landmark.name.localeCompare(b.m.landmark.name))
    .slice(0, limit)
    .map((s) => s.m);
}

/** The closest landmark within `within` metres. */
export function nearestLandmark(at: LatLng, within = 600, kinds: Landmark['kind'][] = ['spot', 'place']): Landmark | null {
  let best: Landmark | null = null;
  let bestD = within;
  for (const l of LANDMARKS) {
    if (!kinds.includes(l.kind)) continue;
    const d = haversine(at, l);
    if (d <= bestD) {
      best = l;
      bestD = d;
    }
  }
  return best;
}

/** A spoken-style address for a point: "Main Bazaar, near SBI ATM". */
export function describePoint(at: LatLng): { title: string; short: string } {
  const place = nearestLandmark(at, 1500, ['place']);
  const spot = nearestLandmark(at, 250, ['spot']);
  const base = place?.name ?? 'Pinned spot';
  if (spot) return { title: `${base}, near ${spot.name}`, short: base };
  if (place) return { title: haversine(at, place) < 150 ? base : `Near ${base}`, short: base };
  return { title: `Pinned spot · ${at.lat.toFixed(4)}, ${at.lng.toFixed(4)}`, short: 'Pinned spot' };
}

/** "Main Bazaar" from an address like "Main Bazaar, near SBI ATM" (trip cards, chips). */
export const shortName = (address: string | null | undefined) => (address ?? 'Pinned spot').split(',')[0].trim();

/** Where the app starts when the phone gives no location: Main Bazaar, Jourian. */
export const DEFAULT_PICKUP = LANDMARKS[0];
